/**
 * EL DESPLEGABLE DEL @ — UNO SOLO, PARA TODAS LAS CAJAS DE TEXTO.
 *
 * Lissette, 2026-09-12: que nombrar a un compañero en una nota o un comentario
 * le avise. Los hilos son muchos —notas del cliente, comentarios de una nota,
 * notas de la póliza, notas del pago del mes, tareas, tickets, bóveda,
 * reclamos— y cada uno tiene su propia caja de texto, escrita en su día a su
 * manera. Meter un desplegable dentro de cada una habría sido escribir ocho
 * veces el mismo menú.
 *
 * Así que esto es un GANCHO, no un componente que sustituya la caja: se le pasa
 * el valor y su setter, devuelve dos manejadores para enchufar a la caja que ya
 * existe y el panel para pintar debajo. La caja no cambia de dueño.
 *
 * La lógica de verdad (cuándo hay una mención empezada, quién encaja, cómo se
 * inserta) vive en `crm-app/menciones.js`, sin React, para poder probarla.
 *
 * IMPORTANTE: esto no decide a quién se avisa. Eso es del servidor
 * (`utils/menciones.ts`), que busca los nombres del equipo dentro del texto
 * guardado. El menú solo ayuda a escribirlo bien — una mención tecleada a mano
 * llega igual.
 */
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { consultaDeMencion, filtrarCompaneros, insertarMencion } from '../menciones.js';
import { equipoActual, alCambiarElEquipo } from '../equipoParaMenciones.js';

/**
 * El equipo: el que le pasen, y si no el que hay publicado arriba.
 *
 * La mayoría de las cajas de texto están a cinco o seis componentes de donde
 * vive la lista de usuarios, y esos componentes intermedios no tienen nada que
 * ver con el equipo. Ver `equipoParaMenciones.js` para el porqué.
 */
function useEquipo(usuarios) {
  const [global, setGlobal] = useState(equipoActual);
  useEffect(() => alCambiarElEquipo(setGlobal), []);
  return (usuarios && usuarios.length) ? usuarios : global;
}

/**
 * @param {object}   p
 * @param {Array}    p.usuarios  El equipo: [{ id, name }] tal como lo trae api.js.
 * @param {Function} p.onValor   Cómo se cambia ese texto.
 * @param {boolean}  [p.activo]  A false, el gancho no hace nada (modo demo, etc.).
 */
export function useMenciones({ usuarios, onValor, activo = true }) {
  const refCaja = useRef(null);
  const equipo = useEquipo(usuarios);
  const [abierto, setAbierto] = useState(null); // { desde, consulta }
  const [elegido, setElegido] = useState(0);

  const opciones = useMemo(
    () => (abierto ? filtrarCompaneros(equipo, abierto.consulta) : []),
    [abierto, equipo],
  );

  const cerrar = useCallback(() => { setAbierto(null); setElegido(0); }, []);

  // Se relee la consulta en CADA cambio, no solo al teclear la arroba: así el
  // menú también se abre al pegar texto o al mover el cursor con el ratón hasta
  // una mención a medio escribir.
  const revisar = useCallback((caja) => {
    // Sin equipo no hay nada que ofrecer, y este gancho está enchufado a TODAS
    // las cajas de notas de la aplicación: cortar aquí evita un repintado por
    // pulsación en cada una de ellas mientras la lista aún no ha cargado.
    if (!activo || !caja || !equipo.length) return cerrar();
    const q = consultaDeMencion(caja.value, caja.selectionStart);
    if (!q) return cerrar();
    setAbierto(q);
    setElegido(0);
  }, [activo, cerrar, equipo]);

  const onChange = useCallback((e) => {
    onValor(e.target.value);
    revisar(e.target);
  }, [onValor, revisar]);

  const elegir = useCallback((u) => {
    const caja = refCaja.current;
    if (!caja || !abierto) return;
    const r = insertarMencion(caja.value, abierto.desde, caja.selectionStart, u.nombre);
    onValor(r.texto);
    cerrar();
    // El cursor se coloca DESPUÉS de que React repinte el valor; hacerlo antes
    // lo deja donde estaba y el siguiente carácter cae en mitad del nombre.
    requestAnimationFrame(() => {
      try { caja.focus(); caja.setSelectionRange(r.cursor, r.cursor); } catch { /* la caja ya no está */ }
    });
  }, [abierto, onValor, cerrar]);

  // Con el menú abierto, las flechas y el Enter son del menú, no de la caja: por
  // eso `preventDefault`. Sin él, el Enter enviaría la nota a medio nombre.
  const onKeyDown = useCallback((e) => {
    if (!abierto || !opciones.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setElegido((i) => (i + 1) % opciones.length); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setElegido((i) => (i - 1 + opciones.length) % opciones.length); return; }
    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); elegir(opciones[elegido] || opciones[0]); return; }
    if (e.key === 'Escape') { e.preventDefault(); cerrar(); }
  }, [abierto, opciones, elegido, elegir, cerrar]);

  // `onBlur` con retraso: sin él, el clic en una opción cierra el menú antes de
  // que llegue el clic y no se elige nada.
  const onBlur = useCallback(() => { setTimeout(cerrar, 150); }, [cerrar]);

  const panel = (abierto && opciones.length)
    ? <ListaDeMenciones opciones={opciones} elegido={elegido} onElegir={elegir} />
    : null;

  return { ref: refCaja, onChange, onKeyDown, onBlur, onClick: (e) => revisar(e.target), panel, abierto: !!panel };
}

/**
 * LA MISMA AYUDA, PERO EN EL EDITOR DE NOTAS.
 *
 * `NoteEditor` (ui.jsx) no es un `<textarea>`: es un `contentEditable` con
 * marcas de negrita y resaltado dentro. No tiene `selectionStart` ni
 * `setSelectionRange`, así que el gancho de arriba no le sirve.
 *
 * La clave para que esto sea SEGURO es que **solo se toca el nodo de texto
 * donde está el cursor**. Ni se lee ni se reescribe el HTML de la nota, así que
 * las marcas de formato no pueden romperse: como mucho, si algo no encaja, no
 * sale el menú — y escribir `@María Fernanda` a mano sigue funcionando igual,
 * porque quien resuelve la mención es el servidor.
 *
 * Por eso cada paso va en `try`: el peor caso aceptable es que la ayuda no
 * aparezca, nunca que la nota salga cambiada.
 *
 * @param {object}   p
 * @param {Array}    p.usuarios  El equipo: [{ id, name }].
 * @param {Function} p.alInsertar Llamado tras insertar, para que el editor
 *                                vuelva a publicar su texto (su `emit`).
 * @param {boolean}  [p.activo]
 */
export function useMencionesEnEditable({ usuarios, alInsertar, activo = true }) {
  const equipo = useEquipo(usuarios);
  const [abierto, setAbierto] = useState(null); // { nodo, desde, hasta, consulta }
  const [elegido, setElegido] = useState(0);

  const opciones = useMemo(
    () => (abierto ? filtrarCompaneros(equipo, abierto.consulta) : []),
    [abierto, equipo],
  );

  const cerrar = useCallback(() => { setAbierto(null); setElegido(0); }, []);

  const revisar = useCallback(() => {
    // Ver la nota del gancho de arriba: sin equipo, ni se mira.
    if (!activo || !equipo.length) return cerrar();
    try {
      const sel = typeof window !== 'undefined' ? window.getSelection() : null;
      const nodo = sel && sel.anchorNode;
      // Solo dentro de un nodo de TEXTO: si el cursor está entre etiquetas no
      // hay nada que leer, y forzarlo es justo lo que rompería el formato.
      if (!nodo || nodo.nodeType !== 3) return cerrar();
      const q = consultaDeMencion(nodo.textContent || '', sel.anchorOffset);
      if (!q) return cerrar();
      setAbierto({ nodo, desde: q.desde, hasta: sel.anchorOffset, consulta: q.consulta });
      setElegido(0);
    } catch { cerrar(); }
  }, [activo, cerrar, equipo]);

  const elegir = useCallback((u) => {
    if (!abierto) return;
    try {
      const { nodo, desde, hasta } = abierto;
      const texto = nodo.textContent || '';
      const pegado = `@${String(u.nombre || '').trim()} `;
      const derecha = texto.slice(hasta);
      nodo.textContent = texto.slice(0, desde) + pegado + (derecha.startsWith(' ') ? derecha.slice(1) : derecha);
      const pos = desde + pegado.length;
      const sel = window.getSelection();
      const r = document.createRange();
      r.setStart(nodo, Math.min(pos, (nodo.textContent || '').length));
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    } catch { /* el nodo cambió bajo los pies: se queda lo tecleado */ }
    cerrar();
    // El editor publica su texto a partir del DOM, así que hay que pedírselo:
    // cambiar el nodo a mano no dispara su `onInput`.
    try { alInsertar?.(); } catch { /* el editor ya no está montado */ }
  }, [abierto, cerrar, alInsertar]);

  const onKeyDown = useCallback((e) => {
    if (!abierto || !opciones.length) return false;
    if (e.key === 'ArrowDown') { e.preventDefault(); setElegido((i) => (i + 1) % opciones.length); return true; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setElegido((i) => (i - 1 + opciones.length) % opciones.length); return true; }
    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); elegir(opciones[elegido] || opciones[0]); return true; }
    if (e.key === 'Escape') { e.preventDefault(); cerrar(); return true; }
    return false;
  }, [abierto, opciones, elegido, elegir, cerrar]);

  const panel = (abierto && opciones.length)
    ? <ListaDeMenciones opciones={opciones} elegido={elegido} onElegir={elegir} />
    : null;

  return { revisar, onKeyDown, onBlur: () => setTimeout(cerrar, 150), panel };
}

/** El menú en sí. Se pinta pegado a la caja, así que su contenedor va relativo. */
function ListaDeMenciones({ opciones, elegido, onElegir }) {
  return (
    <div style={{
      position: 'absolute', zIndex: 60, left: 0, right: 0, top: '100%', marginTop: 2,
      background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
      borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.35)', overflow: 'hidden', maxHeight: 210, overflowY: 'auto',
    }}>
      {opciones.map((u, i) => (
        <button
          key={u.id}
          type="button"
          // `onMouseDown` y no `onClick`: el clic normal llega después del blur
          // de la caja, y para entonces el menú ya se cerró.
          onMouseDown={(e) => { e.preventDefault(); onElegir(u); }}
          style={{
            display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
            padding: '.4rem .6rem', fontSize: '.78rem', fontFamily: 'inherit',
            background: i === elegido ? 'var(--c-surface3,#162d44)' : 'transparent',
            color: 'var(--c-text,#eef6ff)',
          }}
        >
          <span style={{ color: 'var(--c-cyan,#00d4ff)', fontWeight: 700 }}>@</span>{u.nombre}
        </button>
      ))}
    </div>
  );
}
